import { resolveIconHref } from "../../icons/resolve-icon.js";
import { cn } from "tailwind-variants";
import { Button, LinkButton } from "#/_packages/umbrella/features/button/components/button.tsx";

export type LoginLogoutLink = {
	id: string;
	label: string;
	href: string;
	target?: "_self" | "_blank" | "_parent" | "_top";
};

export type LogoutButtonStyle = {
	intentColor?:
		| "primary"
		| "secondary"
		| "light"
		| "dark"
		| "white"
		| "black"
		| "success"
		| "warning"
		| "danger"
		| "info"
		| "none";
	size?: "small" | "default" | "large";
	iconPosition?: "start" | "end";
	hideTextLabel?: boolean;
};

export type LoginLogoutProps = {
	isLoggedIn?: boolean;
	loginLinks?: readonly LoginLogoutLink[];
	logoutLinks?: readonly LoginLogoutLink[];
	logoutLabel?: string;
	preview?: boolean;
	intentColor?: LogoutButtonStyle["intentColor"];
	logoutButtonStyle?: LogoutButtonStyle;
	onLogout?: () => void;
	className?: string;
};

function LockIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			aria-hidden="true"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="1.75"
			className="lucide lucide-lock-keyhole icon icon-18"
		>
			<circle cx="12" cy="16" r="1" />
			<rect x="3" y="10" width="18" height="12" rx="2" />
			<path d="M7 10V7a5 5 0 0 1 10 0v3" />
		</svg>
	);
}

function mapButtonSize(size: LogoutButtonStyle["size"]) {
	switch (size) {
		case "small":
			return "small" as const;
		case "large":
			return "large" as const;
		default:
			return "medium" as const;
	}
}

function SpriteIcon({ icon }: { icon: string }) {
	return (
		<svg className="icon" aria-hidden="true">
			<use href={resolveIconHref(icon)} />
		</svg>
	);
}

function TextLabel({
	label,
	hideLabel,
}: {
	label: string;
	hideLabel?: boolean;
}) {
	return hideLabel ? (
		<span className="sr-only">{label}</span>
	) : (
		<span className="text-label">{label}</span>
	);
}

export function LoginLogout({
	isLoggedIn = false,
	loginLinks = [],
	logoutLinks = [],
	logoutLabel = "Login/Logout",
	preview = false,
	intentColor,
	logoutButtonStyle,
	onLogout,
	className,
}: LoginLogoutProps) {
	if (preview) {
		return (
			<div
				className={cn(
					"flex flex-col gap-2 rounded bg-surface-muted p-4 text-text-base",
					className,
				)}
			>
				<div className="flex flex-wrap items-center justify-start gap-2">
					<LockIcon />
					<span className="leading-tight">{logoutLabel}</span>
				</div>
			</div>
		);
	}

	if (!isLoggedIn) {
		return (
			<>
				{loginLinks.map((link) => (
					<LinkButton
						key={link.id}
						href={link.href}
						target={link.target}
						variant="link"
						intent="primary"
						className={cn(
							"transition-colors hover:text-brand-primary-dim",
							className,
						)}
					>
						<TextLabel
							label={link.label}
							hideLabel={logoutButtonStyle?.hideTextLabel ?? false}
						/>
					</LinkButton>
				))}
			</>
		);
	}

	return (
		<>
			{logoutLinks.map((link) => (
				<div key={link.id} className={cn("logout-button-wrapper", className)}>
					<Button
						type="button"
						onClick={() => onLogout?.()}
						intent={intentColor ?? logoutButtonStyle?.intentColor ?? "primary"}
						size={mapButtonSize(logoutButtonStyle?.size)}
						className={cn(
							logoutButtonStyle?.iconPosition === "end"
								? "flex-row-reverse"
								: "flex-row",
							"transition-colors",
						)}
					>
						{logoutButtonStyle?.iconPosition === "end" ? null : (
							<SpriteIcon icon="log-out" />
						)}
						<TextLabel
							label={link.label}
							hideLabel={logoutButtonStyle?.hideTextLabel ?? false}
						/>
						{logoutButtonStyle?.iconPosition === "end" ? (
							<SpriteIcon icon="log-out" />
						) : null}
					</Button>
				</div>
			))}
		</>
	);
}
